/*
 * cart-drawer-custom.js
 *
 * Behavior for the EU (Prestige) cart-drawer custom design — the native Liquid
 * port of the Intelligems A/B treatment. Loaded from snippets/cart-drawer.liquid
 * only when Settings → Cart → "Enable drawer custom design" is on.
 *
 * Three feature-scoped jobs, none of which touch core cart logic:
 *   1. Upsell "+ ADD TO THE CART" button — posts to /cart/add.js and dispatches
 *      `product:added`, the same event assets/hds-superform.js fires. Prestige's
 *      CartSection listens for it and re-renders + opens the drawer.
 *   2. Free-shipping truck icon — injected into the progress bar and positioned
 *      to ride the fill. Re-runs after the theme swaps the `.Cart` node.
 *   3. Cart-count bubble — the drawer title lives outside the `.Cart` node the
 *      theme re-renders, so its bubble is kept in sync from `.Header__CartCount`.
 *
 * Everything is delegated / observer-driven so it survives Prestige's partial
 * re-render (CartSection._replaceContent swaps only the `.Cart` node).
 */
(() => {
  if (window.__cartDrawerCustomInit) return;
  window.__cartDrawerCustomInit = true;

  const BODY_CLASS = 'cart-drawer-custom-design';
  const isActive = () => document.body.classList.contains(BODY_CLASS);

  /* Inline truck SVG — used when no custom shipping icon is configured. */
  const TRUCK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="12" viewBox="0 0 19 12" fill="none"><path d="M0.3125 4.12415C0.139771 4.12415 0 4.26407 0 4.43665C0 4.60922 0.139771 4.74915 0.3125 4.74915H1.62539L0.354004 9.66919C0.32959 9.76273 0.350037 9.86221 0.409241 9.93866C0.468445 10.0151 0.559692 10.0598 0.656433 10.0598H2.3163C2.46603 10.907 3.20581 11.553 4.09546 11.553C4.9851 11.553 5.72489 10.907 5.87461 10.0598H11.7694H12.8977C13.0474 10.907 13.7872 11.553 14.6768 11.553C15.5665 11.553 16.3062 10.907 16.456 10.0598H17.3481C17.3485 10.0598 17.3489 10.06 17.3492 10.06C17.388 10.0599 17.425 10.051 17.4603 10.0373C17.4723 10.0327 17.4823 10.0258 17.4936 10.0198C17.5152 10.0083 17.5354 9.99544 17.5539 9.97911C17.5647 9.96962 17.5738 9.9596 17.5831 9.94873C17.5982 9.93128 17.6106 9.91234 17.6217 9.89147C17.6287 9.87825 17.6354 9.86565 17.6405 9.85138C17.6431 9.84406 17.648 9.83826 17.6501 9.83063L18.7387 5.89569C18.765 5.80032 18.7448 5.69809 18.6838 5.62012L16.4441 2.75101C16.3849 2.67517 16.2939 2.63077 16.1978 2.63077H13.9312L14.5102 0.390625C14.5346 0.297089 14.5142 0.197601 14.455 0.121155C14.3958 0.0447083 14.3045 0 14.2078 0H3.09448C2.95197 0 2.82745 0.0964356 2.79205 0.234375L2.22181 2.4411H0.656433C0.483704 2.4411 0.343933 2.58102 0.343933 2.7536C0.343933 2.92618 0.483704 3.0661 0.656433 3.0661H2.0603L1.7869 4.12415H0.3125ZM4.09546 10.928C3.4453 10.928 2.91643 10.4008 2.91267 9.75149C2.91269 9.75002 2.91351 9.7488 2.91351 9.74731C2.91351 9.74522 2.91237 9.74348 2.91233 9.7414C2.91416 9.09033 3.44412 8.56125 4.09546 8.56125C4.74609 8.56125 5.27542 9.08928 5.2784 9.7394C5.27832 9.74218 5.27679 9.74451 5.27679 9.74731C5.27679 9.74951 5.278 9.75132 5.27803 9.75349C5.27319 10.4018 4.74491 10.928 4.09546 10.928ZM14.6768 10.928C14.0256 10.928 13.4958 10.3992 13.4937 9.74846C13.4937 9.74806 13.494 9.74771 13.494 9.74731C13.494 9.74628 13.4934 9.74545 13.4934 9.74443C13.4935 9.09195 14.0244 8.56125 14.6768 8.56125C15.3293 8.56125 15.8603 9.0921 15.8603 9.74472C15.8603 10.3972 15.3293 10.928 14.6768 10.928ZM16.0452 3.25577L18.0945 5.88074L17.1111 9.43481H16.4568C16.3091 8.58509 15.5683 7.93625 14.6768 7.93625C13.7854 7.93625 13.0445 8.58509 12.8969 9.43481H12.1728L13.7697 3.25577H16.0452ZM2.70567 3.0661H5.24139C5.41412 3.0661 5.55389 2.92618 5.55389 2.7536C5.55389 2.58102 5.41412 2.4411 5.24139 2.4411H2.86718L3.33649 0.625H13.8043L11.5274 9.43481H5.87542C5.72779 8.58509 4.98692 7.93625 4.09546 7.93625C3.204 7.93625 2.46315 8.58509 2.31552 9.43481H1.05988L1.83584 6.43204H6.03149C6.20422 6.43204 6.34399 6.29211 6.34399 6.11954C6.34399 5.94696 6.20422 5.80704 6.03149 5.80704H1.99736L2.27074 4.74915H6.37543C6.54816 4.74915 6.68793 4.60922 6.68793 4.43665C6.68793 4.26407 6.54816 4.12415 6.37543 4.12415H2.43225L2.70567 3.0661Z" fill="white"/></svg>`;

  const drawerRoot = () => document.getElementById('sidebar-cart');

  /* ---------- 1. upsell add-to-cart ---------- */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('.cart-upsell-add');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) {
      console.warn('[cart-drawer-custom] missing data-variant-id');
      return;
    }

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      const params = new URLSearchParams({ id: variantId, quantity: '1' });
      if (btn.dataset.sellingPlan) params.append('selling_plan', btn.dataset.sellingPlan);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: params,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (data && data.status && Number(data.status) >= 400) {
        throw new Error(data.description || data.message || 'add ' + data.status);
      }

      // Same event Prestige's super form fires — CartSection re-renders + opens the drawer.
      document.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: { variant: data, quantity: 1 },
        })
      );

      btn.textContent = '✓ Added';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1200);
    } catch (err) {
      console.error('[cart-drawer-custom] upsell add failed', err);
      btn.textContent = 'Try again';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    }
  });

  /* ---------- 2. free-shipping truck icon ---------- */
  const positionShippingIcons = () => {
    const drawer = drawerRoot();
    if (!drawer) return;
    const iconUrl = drawer.getAttribute('data-cart-v2-shipping-icon');

    drawer.querySelectorAll('.cart-shippingThreshold__bar').forEach((bar) => {
      const progress = bar.querySelector('.cart-shippingThreshold__progress');
      if (!progress) return;

      const match = (progress.getAttribute('style') || '').match(/width:\s*([\d.]+)%/);
      const widthValue = match ? parseFloat(match[1]) : 0;
      let pct = Math.round(widthValue);
      let offset = 10;
      if (widthValue >= 100) {
        pct = 100;
        offset = 25;
      } else if (widthValue >= 95) {
        pct = 95;
        offset = 25;
      }

      let icon = bar.querySelector(':scope > .shipping-icon');
      if (!icon) {
        icon = document.createElement('span');
        icon.className = 'shipping-icon';
        icon.innerHTML = iconUrl ? `<img src="${iconUrl}" alt="Shipping Icon">` : TRUCK_SVG;
        bar.appendChild(icon);
      }
      const desiredLeft = `calc(${pct}% - ${offset}px)`;
      if (icon.style.left !== desiredLeft) icon.style.left = desiredLeft;
    });
  };

  /* ---------- 3. cart-count bubble (title lives outside the re-rendered .Cart) ---------- */
  const syncCountBubble = () => {
    const title = document.querySelector('#sidebar-cart .Drawer__Title');
    if (!title) return;
    const bubble = title.querySelector('.cart-count-bubble');
    if (!bubble) return;
    const source = document.querySelector('.Header__CartCount');
    if (!source) return;
    const n = source.textContent.trim();
    if (n !== '' && bubble.textContent.trim() !== n) bubble.textContent = n;
  };

  const apply = () => {
    if (!isActive()) return;
    positionShippingIcons();
    syncCountBubble();
  };

  apply();

  // Re-apply after the theme re-renders the drawer (add / remove / qty change).
  document.addEventListener('product:added', () => setTimeout(apply, 60));

  const observed = drawerRoot() || document.body;
  let debounce;
  new MutationObserver(() => {
    clearTimeout(debounce);
    debounce = setTimeout(apply, 80);
  }).observe(observed, { childList: true, subtree: true });
})();
